import React from 'react';
import { getTemplateColor } from '../utils/templateColor';

/**
 * earlierRunsMonthLabel
 *
 * Compact month-range summary for the collapsed "Show N earlier counts"
 * control, e.g. "Feb–Jun" or "Jul" (3-letter abbreviations, one line).
 * Only runs dated within the last 12 months contribute; returns '' when
 * none qualify so callers can omit the suffix entirely.
 */
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function earlierRunsMonthLabel(hiddenRuns) {
  if (!hiddenRuns || hiddenRuns.length === 0) return '';
  const now = new Date();
  const windowStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  let earliest = null;
  let latest = null;
  hiddenRuns.forEach((s) => {
    const raw = s.completed_at || s.session_date || s.created_at;
    if (!raw) return;
    // Date-only strings parse as UTC midnight; anchor to local time instead
    // so a count doesn't drift into the previous month near month boundaries.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(String(raw)) ? new Date(raw + 'T00:00:00') : new Date(raw);
    if (isNaN(d.getTime()) || d < windowStart) return;
    if (!earliest || d < earliest) earliest = d;
    if (!latest || d > latest) latest = d;
  });
  if (!earliest) return '';
  const from = MONTH_ABBR[earliest.getMonth()];
  const to = MONTH_ABBR[latest.getMonth()];
  // A 12-month window never repeats a month name, so equal abbreviations
  // mean the same calendar month.
  return from === to ? from : `${from}–${to}`;
}

/**
 * SessionTimelineStack
 *
 * Shared presentational shell for the "template at top, runs stacked underneath
 * as a timeline" UI used in both the Count Overview and the Reports session picker.
 *
 * Props
 * ─────
 * groups              : Array<{ groupId, template, runs, name, isOrder, isEOP }>
 *                       `runs` must be pre-sorted newest-first by the caller.
 * isMobile            : bool
 * expandedRunsSet     : Set<string>  which groups have ALL runs expanded
 * onToggleExpandedRuns: (groupId: string) => void
 * renderRunRow        : (session, ctx) => JSX
 *                       ctx = { isNewest, isFirst, isLast, index, groupId, runsToDisplay }
 *                       Render a single run row (checkbox for Reports, full card for CountInterface).
 * formatDate          : (dateStr) => string
 *
 * Optional — header
 * ─────────────────
 * renderHeader        : (group, latestRun, { isExpanded }) => JSX
 *                       When provided, replaces the entire header row.
 *                       Useful for CountInterface's chevron + SVG icons + action buttons.
 * renderGroupActions  : (group, latestRun) => JSX   right-side header slot
 * renderGroupMeta     : (group, latestRun) => JSX   extra row below the subtitle
 * onGroupHeaderClick  : (groupId: string) => void   header click handler
 *
 * Optional — runs area
 * ────────────────────
 * expandedOrder       : 'newest-first' | 'oldest-first'
 *                       Controls run order when the stack is fully expanded.
 *                       Default: 'newest-first'.
 *                       Use 'oldest-first' for Count Overview's chronological timeline.
 * spineMarginLeft     : string   css value for the left indent of the spine (default '14px')
 * runsAreaTopBorder   : bool     whether to add 1px top border above runs area (default true)
 * containerSpine      : bool     whether the runs container renders the shared spine borderLeft.
 *                       Set to false when renderRunRow already carries its own spine (e.g. CountInterface
 *                       event markers which each have their own borderLeft at connectorLeft).
 * runsContainerPadding: string   CSS shorthand for the runs container's padding.
 *                       Default '4px 10px 8px 18px'. Override when renderRunRow manages its own
 *                       left offsets (e.g. CountInterface uses marginLeft: connectorLeft internally).
 * runsAreaBackground  : string   Optional background color for the runs/timeline area below the
 *                       2px brown divider. Lets child runs sit on a lighter shade than the darker
 *                       template parent card so the "template ends / counts begin" boundary is
 *                       clear. Default null (inherits the card background).
 *
 * Optional — expanded details
 * ───────────────────────────
 * renderExpandedPanel : (group) => JSX  panel rendered after the runs area (Count Overview metadata)
 *
 * Optional — grouped sections (e.g. month buckets)
 * ────────────────────────────────────────────────
 * groupedSections     : Array<{ key: string, label: string, groups: Array }>
 *                       When provided, replaces the flat `groups` rendering with
 *                       collapsible section headers (one per bucket). Works on
 *                       desktop and mobile.
 * expandedSections    : Set<string>  which section keys are currently expanded.
 *                       When omitted, sections render uncollapsible (all open).
 * onToggleSection     : (key: string) => void
 * emptyMessage        : string  shown when groups is empty
 */
export function SessionTimelineStack({
  groups,
  isMobile = false,
  expandedRunsSet,
  onToggleExpandedRuns,
  renderRunRow,
  formatDate,
  renderHeader = null,
  renderGroupActions = null,
  renderGroupMeta = null,
  onGroupHeaderClick = null,
  expandedOrder = 'newest-first',
  spineMarginLeft = '14px',
  runsAreaTopBorder = true,
  containerSpine = true,
  runsContainerPadding = '4px 10px 8px 18px',
  renderExpandedPanel = null,
  groupedSections = null,
  expandedSections = null,
  onToggleSection = null,
  emptyMessage = 'No sessions available.',
  compactHiddenRunsControl = false,
  suppressHiddenRunsControl = false,
  renderTimelineStart = null,
  renderChildlessTerminator = null,
  runsAreaBackground = null,
}) {
  const SPINE = '2px solid #D9CEC2';

  const renderStack = (group) => {
    const { groupId, template, runs, name, isOrder, isEOP } = group;
    const isTemplateParent = !!template;
    const latestRun = runs[0] || null;
    const isExpanded = expandedRunsSet.has(groupId);
    const hiddenCount = runs.length > 1 ? runs.length - 1 : 0;
    // User-facing terminology: order groups collapse "orders", everything
    // else (counts, EOP counts) collapses "counts" — never "runs".
    const hiddenNoun = isOrder ? 'order' : 'count';
    const hiddenMonths = hiddenCount > 0 ? earlierRunsMonthLabel(runs.slice(1)) : '';
    // Template accent: keeps recognition consistent across cards/reports.
    // Falls back to a deterministic palette pick on the run's template_id
    // when the live template row has been deleted but historical runs remain.
    const accentColor = getTemplateColor(template || {
      id: latestRun?.template_id ?? null,
      name: name || latestRun?.template_name_snapshot || '',
    });

    let runsToDisplay;
    if (!isExpanded) {
      runsToDisplay = latestRun ? [latestRun] : [];
    } else if (expandedOrder === 'oldest-first') {
      runsToDisplay = [...runs].reverse();
    } else {
      runsToDisplay = runs;
    }

    const defaultHeader = isMobile ? (
      /* Mobile: stacked vertical priority — title → schedule/meta → badges → actions */
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: '3px',
          padding: '10px 10px',
          cursor: onGroupHeaderClick ? 'pointer' : 'default',
          borderRadius: isTemplateParent ? '9px 9px 0 0' : undefined,
        }}
        onClick={onGroupHeaderClick ? () => onGroupHeaderClick(groupId) : undefined}
      >
        {/* Row 1: icon + title (full width, up to 2 lines) */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
          <span style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1.3, paddingTop: '1px' }}>
            {isOrder ? '🚚' : isEOP ? '📅' : '📋'}
          </span>
          <span style={{
            fontWeight: 700, fontSize: '1rem', color: accentColor,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', flex: 1, minWidth: 0, lineHeight: 1.3,
          }}>
            {name}
          </span>
        </div>
        {/* Row 2: schedule / frequency + items metadata */}
        <div style={{ fontSize: '0.75rem', color: '#8B7B73', paddingLeft: 'calc(1rem + 7px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isTemplateParent ? (
            <span>
              Recurring
              {runs.length > 0
                ? ` · ${runs.length} run${runs.length === 1 ? '' : 's'}`
                : ' · No runs yet'}
            </span>
          ) : latestRun ? (
            <span>
              {formatDate(latestRun.completed_at || latestRun.session_date || latestRun.created_at)}
              {' · '}{(() => { const c = latestRun.items_counted || 0; const t = latestRun.total_items || 0; const denom = t > 0 ? t : c; return denom > 0 ? `${c}/${denom} items` : '0 items'; })()}
            </span>
          ) : null}
        </div>
        {/* Row 3: badge row — wraps as needed */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingLeft: 'calc(1rem + 7px)', marginTop: '1px' }}>
          {isTemplateParent ? (
            <span style={{
              fontSize: '0.65rem', fontWeight: 600, padding: '2px 8px',
              borderRadius: '12px', backgroundColor: '#EDE6DE', color: '#5A4A42', whiteSpace: 'nowrap',
            }}>
              Template
            </span>
          ) : latestRun ? (
            <span className={`badge-status ${
              latestRun.status === 'completed' ? 'badge-status-complete'
                : (latestRun.status === 'in_progress' || latestRun.status === 'paused') ? 'badge-status-inprogress'
                  : 'badge-status-draft'
            }`}>
              {latestRun.status === 'completed' ? 'Complete'
                : latestRun.status === 'in_progress' ? 'In Progress'
                  : latestRun.status === 'paused' ? 'Saved'
                    : 'Draft'}
            </span>
          ) : null}
          {runs.length > 1 && (
            <span style={{
              fontSize: '0.65rem', fontWeight: 600, padding: '2px 6px',
              borderRadius: '12px', backgroundColor: '#5A4A42', color: '#FAF6F2',
            }}>
              {runs.length}×
            </span>
          )}
        </div>
        {renderGroupMeta && renderGroupMeta(group, latestRun)}
        {renderGroupActions && (
          <div style={{ paddingLeft: 'calc(1rem + 7px)', marginTop: '2px' }}>
            {renderGroupActions(group, latestRun)}
          </div>
        )}
      </div>
    ) : (
      /* Desktop: existing horizontal layout — unchanged */
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '11px 14px',
          cursor: onGroupHeaderClick ? 'pointer' : 'default',
          borderRadius: isTemplateParent ? '9px 9px 0 0' : undefined,
        }}
        onClick={onGroupHeaderClick ? () => onGroupHeaderClick(groupId) : undefined}
      >
        <span style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1 }}>
          {isOrder ? '🚚' : isEOP ? '📅' : '📋'}
        </span>

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
            <span style={{
              fontWeight: 700,
              fontSize: '0.9rem',
              color: accentColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {name}
            </span>
            {runs.length > 1 && (
              <span style={{
                fontSize: '0.62rem', fontWeight: 600, padding: '2px 6px',
                borderRadius: '12px', backgroundColor: '#5A4A42', color: '#FAF6F2', flexShrink: 0,
              }}>
                {runs.length}×
              </span>
            )}
            {isTemplateParent ? (
              <span style={{
                fontSize: '0.6rem', fontWeight: 600, padding: '2px 8px',
                borderRadius: '12px', backgroundColor: '#EDE6DE', color: '#5A4A42',
                flexShrink: 0, whiteSpace: 'nowrap',
              }}>
                Template
              </span>
            ) : latestRun ? (
              <span className={`badge-status ${
                latestRun.status === 'completed' ? 'badge-status-complete'
                  : (latestRun.status === 'in_progress' || latestRun.status === 'paused') ? 'badge-status-inprogress'
                    : 'badge-status-draft'
              }`}>
                {latestRun.status === 'completed' ? 'Complete'
                  : latestRun.status === 'in_progress' ? 'In Progress'
                    : latestRun.status === 'paused' ? 'Saved'
                      : 'Draft'}
              </span>
            ) : null}
          </div>

          <div style={{ fontSize: '0.71rem', color: '#8B7B73', marginTop: '2px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {isTemplateParent ? (
              <span>
                Recurring count
                {runs.length > 0
                  ? ` · ${runs.length} run${runs.length === 1 ? '' : 's'}`
                  : ' · No runs yet'}
              </span>
            ) : latestRun ? (
              <span>
                Latest: {formatDate(latestRun.completed_at || latestRun.session_date || latestRun.created_at)}
                {' · '}{(() => { const c = latestRun.items_counted || 0; const t = latestRun.total_items || 0; const denom = t > 0 ? t : c; return denom > 0 ? `${c}/${denom} items` : '0 items'; })()}
              </span>
            ) : null}
          </div>

          {renderGroupMeta && renderGroupMeta(group, latestRun)}
        </div>

        {renderGroupActions && renderGroupActions(group, latestRun)}
      </div>
    );

    return (
      <TimelineCard
        key={groupId}
        isTemplateParent={isTemplateParent}
        accentColor={accentColor}
        style={{ position: 'relative', ...(isMobile ? { marginLeft: '1px', marginRight: '1px' } : {}) }}
      >
        {renderHeader ? renderHeader(group, latestRun, { isExpanded }) : defaultHeader}

        {renderExpandedPanel && renderExpandedPanel(group)}

        {runs.length > 0 && (
          <div style={{ borderTop: runsAreaTopBorder ? '2px solid #5A4A42' : 'none', ...(runsAreaBackground ? { backgroundColor: runsAreaBackground, borderRadius: '0 0 9px 9px' } : {}) }}>
            {renderTimelineStart && renderTimelineStart(group, {
              hiddenCount,
              isExpanded,
              onToggle: () => onToggleExpandedRuns(groupId),
            })}
            {hiddenCount > 0 && !suppressHiddenRunsControl && (
              <div style={{ padding: compactHiddenRunsControl ? '3px 8px 1px 16px' : '6px 10px 2px 18px' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpandedRuns(groupId);
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: compactHiddenRunsControl ? '3px' : '5px',
                    fontSize: compactHiddenRunsControl ? '0.72rem' : '0.85rem',
                    fontWeight: 700, color: '#5A4A42', whiteSpace: 'nowrap',
                    border: '1px solid #C9BCAE', backgroundColor: '#FAF6F2',
                    borderRadius: compactHiddenRunsControl ? '10px' : '6px', padding: compactHiddenRunsControl ? '3px 9px' : '5px 12px',
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'background-color 0.1s',
                    minHeight: compactHiddenRunsControl ? '32px' : undefined,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F3EDE7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FAF6F2'; }}
                >
                  <span style={{ fontSize: compactHiddenRunsControl ? '0.6rem' : '0.65rem' }}>{isExpanded ? '▾' : '▸'}</span>
                  {isExpanded
                    ? (compactHiddenRunsControl ? 'Less' : 'Show less')
                    : (
                      <>
                        {compactHiddenRunsControl
                          ? `+${hiddenCount} ${hiddenNoun}${hiddenCount === 1 ? '' : 's'}`
                          : `Show ${hiddenCount} earlier ${hiddenNoun}${hiddenCount === 1 ? '' : 's'}`}
                        {hiddenMonths && <span style={{ fontWeight: 600, opacity: 0.85 }}>&nbsp;·&nbsp;{hiddenMonths}</span>}
                      </>
                    )}
                </button>
              </div>
            )}

            <div style={{ padding: runsContainerPadding, ...(containerSpine ? { borderLeft: SPINE, marginLeft: spineMarginLeft } : {}) }}>
              {runsToDisplay.map((session, index) =>
                renderRunRow(session, {
                  isNewest: session.id === runs[0].id,
                  isFirst: index === 0,
                  isLast: index === runsToDisplay.length - 1,
                  index,
                  groupId,
                  runsToDisplay,
                })
              )}
            </div>
          </div>
        )}

        {runs.length === 0 && isTemplateParent && (
          renderChildlessTerminator ? (
            <div style={{ borderTop: runsAreaTopBorder ? '2px solid #5A4A42' : 'none', ...(runsAreaBackground ? { backgroundColor: runsAreaBackground, borderRadius: '0 0 9px 9px' } : {}) }}>
              {renderTimelineStart && renderTimelineStart(group)}
              {renderChildlessTerminator(group)}
            </div>
          ) : (
            <div style={{
              padding: '6px 14px 10px', fontSize: '0.76rem',
              color: '#A89890', borderTop: '1px solid #EDE6DE',
            }}>
              No completed runs yet for this template.
            </div>
          )
        )}
      </TimelineCard>
    );
  };

  if (groups.length === 0) {
    return <div className="text-center text-muted py-3">{emptyMessage}</div>;
  }

  if (groupedSections) {
    const collapsible = !!expandedSections;
    return (
      <div className="session-timeline">
        {groupedSections.map(({ key, label, groups: sectionGroups }) => {
          const isOpen = collapsible ? expandedSections.has(key) : true;
          return (
            <div key={key} style={{ marginBottom: '14px' }}>
              <div
                role={collapsible ? 'button' : undefined}
                tabIndex={collapsible ? 0 : undefined}
                onClick={collapsible ? () => onToggleSection && onToggleSection(key) : undefined}
                onKeyDown={collapsible ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleSection && onToggleSection(key);
                  }
                } : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontWeight: 700, fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: '#5A4A42',
                  padding: isMobile ? '8px 6px' : '8px 10px',
                  marginBottom: '6px',
                  background: '#F3EDE7',
                  border: '1px solid #E8DDCB',
                  borderRadius: '8px',
                  cursor: collapsible ? 'pointer' : 'default',
                  userSelect: 'none',
                }}
              >
                {collapsible && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block', width: '0.75rem',
                      fontSize: '0.7rem', color: '#8B7B73',
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.12s',
                    }}
                  >
                    ▸
                  </span>
                )}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {label}
                </span>
                <span style={{
                  fontWeight: 600, fontSize: '0.7rem', color: '#5A4A42',
                  background: '#FAF6F2', border: '1px solid #E8DDCB',
                  borderRadius: '10px', padding: '1px 8px', flexShrink: 0,
                }}>
                  {sectionGroups.length}
                </span>
              </div>
              {isOpen && sectionGroups.map((group) => renderStack(group))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="session-timeline">
      {groups.map((group) => renderStack(group))}
    </div>
  );
}

/**
 * TimelineCard
 *
 * Lightweight card-shell used when the full SessionTimelineStack is too rigid
 * (e.g. sub-components within the Count Overview that have their own complex body).
 * Provides the shared visual language: brown left border, cream background that
 * darkens for template parents, and the subtle hover shadow.
 *
 * Props
 * ─────
 * isTemplateParent : bool    applies the darker #E8DDCB template background
 * children         : ReactNode
 * style            : object  extra style overrides
 * rest             : spread onto the wrapper div (key, onClick, etc.)
 */
export function TimelineCard({ isTemplateParent = false, accentColor = '#5A4A42', children, style = {}, ...rest }) {
  return (
    <div
      data-template={isTemplateParent ? 'true' : 'false'}
      style={{
        backgroundColor: isTemplateParent ? '#E8DDCB' : '#FAF6F2',
        border: '1px solid #D9CEC2',
        borderRadius: '10px',
        overflow: 'visible',
        borderLeft: `4px solid ${accentColor}`,
        transition: 'box-shadow 0.15s',
        marginBottom: '6px',
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(90,74,66,0.1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      {...rest}
    >
      {children}
    </div>
  );
}

export default SessionTimelineStack;
